import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { FadeInView } from "../../ui/fade-in";
import "./stories.css";

import carterPic from "../../../assets/text-testimonials/carter.webp";
import sarahPic from "../../../assets/text-testimonials/sarah-convery-testimonial.webp";
import ariellePic from "../../../assets/text-testimonials/arielle-testimonial-pic.webp";
import jacksonPic from "../../../assets/text-testimonials/jackson.webp";

import pjt from "../../../assets/text-testimonials/pjt.webp";
import akin from "../../../assets/text-testimonials/akin.webp";
import newYorkLife from "../../../assets/text-testimonials/new-york-life.webp";
import tiaa from "../../../assets/text-testimonials/TIAA.webp";
import ey from "../../../assets/text-testimonials/EY.svg.webp";
import pwc from "../../../assets/text-testimonials/pwc.webp";
import google from "../../../assets/text-testimonials/google.webp";
import gagosian from "../../../assets/text-testimonials/Gagosian.webp";
import ruderFinn from "../../../assets/text-testimonials/Ruder Finn.webp";
import ambit from "../../../assets/text-testimonials/ambit.webp";

/* Four angles on the product, not four versions of the same one: keeping the
   conversations straight, working out what you are aiming at, staying
   organised, then what the whole search felt like. Portraits run large here,
   so the roster is limited to the testimonial photos that ship at 500px or
   better. */
const stories = [
  {
    name: "Carter",
    role: "Wealth Management Intern at TIAA",
    quote:
      "My favorite thing about RAIN is being able to track all of my networking and coffee chat conversations easily. The call notes are so much easier than Excel or Google Docs.",
    image: carterPic,
  },
  {
    name: "Sarah",
    role: "Product Solutions Intern at New York Life",
    quote:
      "RAIN helped me explore different areas of marketing, from brand management to digital advertising, and figure out where I fit. It's more than a job search tool; it's helped me discover my path.",
    image: sarahPic,
  },
  {
    name: "Jackson",
    role: "Investment Banking Summer Analyst at PJT Partners",
    quote:
      "The networking tab and tracker made recruiting way easier. RAIN keeps everything organized and efficient. I highly recommend it to anyone job searching.",
    image: jacksonPic,
  },
  {
    name: "Arielle",
    role: "Summer Intern at FGS Global",
    quote:
      "The job search is one of the most stressful things I've been through, but RAIN made it so much easier. Application tracking, networking help, and interview prep are all in one place. I finally felt like I had a real plan instead of just winging it.",
    image: ariellePic,
  },
];

/* Where those stories ended up — every employer mark the testimonial library
   holds, five to a row. Add one by dropping the mark in assets and adding a
   line. This used to be its own "Trusted by job seekers like you" section; it
   reads as the closing line of the stories instead. */
const employers = [
  { name: "PJT Partners", src: pjt },
  { name: "EY", src: ey },
  { name: "PwC", src: pwc },
  { name: "New York Life", src: newYorkLife },
  { name: "TIAA", src: tiaa },
  { name: "Akin Gump", src: akin },
  { name: "Google", src: google },
  { name: "Gagosian", src: gagosian },
  { name: "Ruder Finn", src: ruderFinn },
  { name: "Ambit", src: ambit },
];

const Stories = () => {
  const [active, setActive] = useState(0);
  const story = stories[active];

  return (
    <section className="s-section" id="stories">
      <div className="home-container">
        <div className="s-stories-head">
          <span className="s-eyebrow">Job seeker stories</span>
          <h2 className="s-stories-title">A clearer search feels different.</h2>
        </div>

        <div className="s-stories-grid">
          <figure className="s-stories-portrait">
            <AnimatePresence initial={false}>
              <motion.img
                key={story.name}
                src={story.image}
                alt={story.name}
                style={story.focus ? { objectPosition: story.focus } : undefined}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: { duration: 0.55, ease: "linear" },
                  scale: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
                }}
              />
            </AnimatePresence>
          </figure>

          <div className="s-stories-panel">
            <span className="s-stories-mark" aria-hidden="true">&ldquo;</span>

            <motion.div
              key={story.name}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
            >
              <blockquote className="s-stories-quote">{story.quote}</blockquote>

              <p className="s-stories-name">{story.name}</p>
              <p className="s-stories-role">{story.role}</p>
            </motion.div>

            {/* Stepped in one after another, the same entrance the career
                centre hero uses — held until the row is on screen, since a
                mount animation this far down the page is never seen. */}
            <div className="s-stories-switch">
              {stories.map((item, i) => (
                <FadeInView
                  key={item.name}
                  delay={i * 0.09}
                  className="s-switch-wrap"
                >
                  <button
                    type="button"
                    className={`s-switch${i === active ? " s-switch-on" : ""}`}
                    onClick={() => setActive(i)}
                    aria-pressed={i === active}
                  >
                    <img decoding="async" loading="lazy" src={item.image} alt="" />
                    <span>{item.name}</span>
                  </button>
                </FadeInView>
              ))}
            </div>
          </div>
        </div>

        <div className="s-stories-employers">
          <p className="s-stories-employers-lead">
            RAIN users have landed roles at
          </p>

          <ul className="s-stories-logos">
            {employers.map(({ name, src }) => (
              <li key={name}>
                <span className="s-stories-logo">
                  <img decoding="async" src={src} alt="" loading="lazy" />
                </span>
                <span className="s-stories-employer">{name}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default Stories;
